import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

interface MenuItem {
  label: string;
  icon?: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface MenuDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  items: MenuItem[];
  trigger: React.ReactNode;
  onTriggerClick: () => void;
  position?: 'top' | 'bottom';
  align?: 'left' | 'right';
}

const MenuContainer = styled.div`
  position: relative;
  display: inline-block;
  overflow: visible;
  z-index: 1900;
`;

const TriggerButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.gray.dark};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;

  &:hover {
    background: ${({ theme }) => theme.colors.gray.light};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const Dropdown = styled.div<{ $show: boolean; $position: 'top' | 'bottom'; $align: 'left' | 'right' }>`
  position: absolute;
  ${({ $position }) => $position === 'top' ? 'bottom: 100%;' : 'top: 100%;'}
  ${({ $align }) => $align === 'left' ? 'left: 0;' : 'right: 0;'}
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 150px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 2000;
  pointer-events: ${({ $show }) => $show ? 'auto' : 'none'};
  display: ${({ $show }) => $show ? 'block' : 'none'};
  opacity: ${({ $show }) => $show ? 1 : 0};
  visibility: ${({ $show }) => $show ? 'visible' : 'hidden'};
  transform: ${({ $show, $position }) => {
    const translateY = $position === 'top' ? '-4px' : '4px';
    return $show ? `translateY(${translateY})` : 'translateY(0)';
  }};
  transition: all ${({ theme }) => theme.transitions.fast};
`;

const MenuItemButton = styled.button<{ $variant?: 'default' | 'danger' }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ $variant, theme }) => $variant === 'danger' ? theme.colors.error : theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  white-space: nowrap;
  position: relative;
  z-index: 1;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.gray.light};
  }

  &:active:not(:disabled) {
    background: ${({ theme }) => theme.colors.gray.medium};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.gray.light};
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.gray.light};
  margin: ${({ theme }) => theme.spacing.xs} 0;
`;

export function MenuDropdown({
  isOpen,
  onClose,
  items,
  trigger,
  onTriggerClick,
  position = 'top',
  align = 'right',
}: MenuDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      onClose();
    }
  }, [onClose]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, handleClickOutside, handleKeyDown]);

  const handleItemClick = (item: MenuItem) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!item.disabled) {
      item.onClick();
      onClose();
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onTriggerClick();
  };

  return (
    <MenuContainer ref={containerRef} data-menu-container>
      <TriggerButton onClick={handleTriggerClick} aria-haspopup="true" aria-expanded={isOpen}>
        {trigger}
      </TriggerButton>
      <Dropdown $show={isOpen} $position={position} $align={align} role="menu">
        {items.map((item, index) => (
          <MenuItemButton
            key={index}
            onClick={handleItemClick(item)}
            $variant={item.variant}
            disabled={item.disabled}
            role="menuitem"
          >
            {item.icon && <span>{item.icon}</span>}
            {item.label}
          </MenuItemButton>
        ))}
      </Dropdown>
    </MenuContainer>
  );
}

export { MenuDivider };
export type { MenuItem, MenuDropdownProps };
